import type { Paragraph, Table } from "docx";
import { SKRData, SKRFraga } from "@/types/skr";
import { downloadWordDocument } from "@/lib/documentGenerator/exportHistory";
import { createStampForCurrentUser } from "@/lib/documentGenerator/watermark";
import {
  body,
  buildDocument,
  factTable,
  h1,
  signatureBlock,
  table,
} from "@/lib/documentGenerator/docTheme";
import { documentFooter } from "@/lib/documentGenerator/documentFooter";

export interface SKRExportOptions {
  includeProjectInfo?: boolean;
  includeAssessments?: boolean;
  includeSignatures?: boolean;
  includeSummary?: boolean;
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export const generateSKRWordDocument = async (
  skrData: SKRData,
  allQuestions: SKRFraga[] = [],
  options: SKRExportOptions = {}
): Promise<void> => {
  const {
    includeProjectInfo = true,
    includeAssessments = true,
    includeSignatures = true,
    includeSummary = true,
  } = options;

  const groupedQuestions = allQuestions.reduce((acc, fraga) => {
    (acc[fraga.kategori] ||= []).push(fraga);
    return acc;
  }, {} as Record<string, SKRFraga[]>);

  const categoryAssessments = Object.keys(groupedQuestions).map((kategori) => {
    const questions = groupedQuestions[kategori];
    const answered = questions.filter((q) => skrData.svar[q.id]?.svar);
    const counts = { låg: 0, medel: 0, hög: 0 };
    answered.forEach((q) => {
      const level = skrData.svar[q.id].svar;
      if (level && level in counts) counts[level as keyof typeof counts]++;
    });
    const predominantLevel = Object.entries(counts).reduce((a, b) =>
      counts[a[0] as keyof typeof counts] > counts[b[0] as keyof typeof counts] ? a : b
    )[0];

    return {
      kategori,
      totalQuestions: questions.length,
      answered: answered.length,
      predominantLevel: answered.length > 0 ? predominantLevel : "ej bedömd",
    };
  });

  const documentName = "SKR-bedömning";
  const stamp = await createStampForCurrentUser({
    projectName: skrData.projektNamn || "",
    docNumber: "03",
    documentName,
  });

  const children: (Paragraph | Table)[] = [
    body("Storlek, komplexitet och risk – underlag för hur projektets arbetsmiljöarbete ska dimensioneras.", {
      italics: true,
      muted: true,
    }),
  ];

  let section = 1;

  if (includeProjectInfo) {
    children.push(
      h1(`${section++}. Projektinformation`),
      factTable([
        ["Projektnamn", skrData.projektNamn],
        ["Beskrivning", skrData.projektbeskrivning],
        ["Budget", skrData.budget],
        ["Byggtid", skrData.byggtid],
      ])
    );
  }

  if (includeSummary) {
    children.push(
      h1(`${section++}. Sammanfattning av bedömning`),
      table(
        ["Kategori", "Bedömning", "Besvarade/Totalt", "Status"],
        categoryAssessments.map((c) => [
          c.kategori,
          capitalize(c.predominantLevel),
          `${c.answered}/${c.totalQuestions}`,
          c.answered === c.totalQuestions ? "Komplett" : "Ofullständig",
        ]),
        { columnWidths: [3800, 1900, 1900, 1760] }
      )
    );
  }

  if (includeAssessments) {
    children.push(h1(`${section++}. Bedömning per kategori`));
    Object.keys(groupedQuestions).forEach((kategori) => {
      children.push(
        h1(kategori),
        table(
          ["Fråga", "Bedömning", "Kommentar", "Åtgärd"],
          groupedQuestions[kategori].map((fraga) => {
            const svar = skrData.svar[fraga.id];
            return [
              fraga.hjalptext ? `${fraga.text}\n${fraga.hjalptext}` : fraga.text,
              svar?.svar ? capitalize(svar.svar) : "Ej bedömd",
              svar?.kommentar,
              svar?.atgard,
            ];
          }),
          { columnWidths: [4200, 1400, 2200, 1560] }
        )
      );
    });
  }

  if (includeSignatures) {
  }
  const footer = await documentFooter({ docNumber: "03", documentType: "skr", signatureRoles: ["Upprättad av", "Byggherre", "Bas-P"] });

  const doc = buildDocument({
    meta: {
      status: footer.status,
      version: footer.version,
      review: footer.review,
      docNumber: "03",
      documentName,
      projectName: skrData.projektNamn || "",
    },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(doc, `SKR-bedömning_${skrData.projektNamn || "Projekt"}.docx`, {
    documentType: "skr",
    docNumber: "03",
    documentName: `SKR-bedömning ${skrData.projektNamn || ""}`.trim(),
    snapshot: skrData,
    stamp,
  });
};
